import React, { useEffect, useState, useRef } from 'react'
import Select from 'react-select'
import SearchIcon from '../../assets/SearchIcon'
import AddIcon from '../../assets/AddIcon'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import MobileHeader from '../Common/Header/MobileHeader'
import { HEADER_CONFIG } from '../../common/constant/common-constant'
import Alert from '../../components/Alert/Alert'
import Card from '../Common/Card/Card'
import PopupCard from '../Common/PopupCard/PopupCard'
import { convertToTitleCase } from '../../common/utils/appUtils'
import {
  getDeliveryPartnersHelper,
  updateDeliveryPartnerHelper,
  addDeliveryPartnerZoneHelper,
  addDeliveryPartnerHelper,
} from '../../helper/settings-helper'
import rideLocations from '../../data/test.ride_locations.json'

const DeliveryManManagement = (props) => {
  const isMobile = useCheckMobileScreen()
  const inputRef = useRef()

  // State for Delivery Partners List
  const [deliveryPartnersList, setDeliveryPartnersList] = useState([])
  const [filteredPartners, setFilteredPartners] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [viewSearch, setViewSearch] = useState(false)

  // State for Drawer & Delivery Partners
  const [open, setOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState(null)
  const [viewMode, setViewMode] = useState('list')
  const [formData, setFormData] = useState({
    name: '',
    mobile_no: '',
    email: '',
    password: '',
    location: [],
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertType, setAlertType] = useState('')

  // State for Delete Confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [partnerToDelete, setPartnerToDelete] = useState(null)

  const [headerOptions, setHeaderOptions] = useState({
    [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
    [HEADER_CONFIG.TITLE]: 'Delivery Man Management',
    [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => props?.onBackClick(),
  })

  // Fetch Delivery Partners on Mount
  useEffect(() => {
    fetchDeliveryPartners()
  }, [])

  // Filter Delivery Partners
  useEffect(() => {
    if (!deliveryPartnersList) return
    const filtered = deliveryPartnersList.filter(
      (partner) =>
        partner?.mobile_no
          ?.toUpperCase()
          ?.includes(searchTerm?.toUpperCase()) ||
        partner?.name?.toUpperCase()?.includes(searchTerm?.toUpperCase())
    )
    setFilteredPartners(filtered)
  }, [searchTerm, deliveryPartnersList])

  const fetchDeliveryPartners = async () => {
    setLoading(true)
    try {
      const data = await getDeliveryPartnersHelper()
      setDeliveryPartnersList(data || [])
      setFilteredPartners(data || [])
    } catch (error) {
      console.error('Error fetching delivery partners:', error)
      showAlert('Failed to fetch delivery partners', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Handle Delivery Partner Card Click
  const handlePartnerClick = async (partner) => {
    setSelectedPartner(partner)
    setOpen(true)
    setViewMode('edit')

    // Populate formData with existing partner data
    setFormData({
      name: partner.name || '',
      mobile_no: partner.mobile_no || '',
      email: partner.email || '',
      password: '', // Password shouldn't be prepopulated for security
      location:
        partner.locations?.map((loc) => ({
          label: loc.zone,
          value: loc.zone,
          latitude: loc.latitude,
          longitude: loc.longitude,
        })) || [],
    })
    setErrors({})
  }

  const handleDrawerToggle = () => {
    setOpen(!open)
    if (open) {
      // Reset on close
      setSelectedPartner(null)
      setViewMode('list')
      setFormData({
        name: '',
        mobile_no: '',
        email: '',
        password: '',
        location: [],
      })
      setErrors({})
    }
  }

  const expandSearch = () => {
    inputRef.current.focus(setViewSearch(true))
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const showAlert = (message, type) => {
    setAlertMessage(message)
    setAlertType(type)
    setTimeout(() => setAlertMessage(''), 3000)
  }

  const validateForm = () => {
    let newErrors = {}
    let isValid = true
    if (viewMode === 'add') {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required'
        isValid = false
      }
      if (!formData.mobile_no.trim()) {
        newErrors.mobile_no = 'Mobile number is required'
        isValid = false
      } else if (!/^\d{11}$/.test(formData.mobile_no)) {
        newErrors.mobile_no = 'Enter a valid 11-digit mobile number'
        isValid = false
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required'
        isValid = false
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Enter a valid email address'
        isValid = false
      }

      if (!formData.password) {
        newErrors.password = 'Password is required'
        isValid = false
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters'
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }

  const handleLocationChange = (selectedOptions) => {
    setFormData({
      ...formData,
      location: selectedOptions || [],
    })
    if (errors.location) {
      setErrors({ ...errors, location: '' })
    }
  }

  const handleAddSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      if (viewMode === 'add') {
        const apiData = {
          name: formData.name,
          mobile_no: formData.mobile_no,
          email: formData.email,
          password: formData.password,
          branch_id: selectedPartner?.id || props?.user?.branch_id,
        }
        const partnerResponse = await addDeliveryPartnerHelper(apiData)
        const newPartnerId = partnerResponse?.delivery_partner_id

        const apiZoneData = {
          delivery_partner_id: newPartnerId,
          locations: formData.location?.map((loc) => {
            const original = rideLocations.find((rl) => rl.name === loc.value)
            return {
              latitude: loc.latitude || original?.long_lat?.[1] || 0,
              longitude: loc.longitude || original?.long_lat?.[0] || 0,
              zone: loc.label,
            }
          }),
        }

        await addDeliveryPartnerZoneHelper(apiZoneData)
        showAlert('Delivery Partner added successfully!', 'success')
      } else {
        const apiData = {
          delivery_partner_id: selectedPartner.id,
          locations: formData.location?.map((loc) => {
            const original = rideLocations.find((rl) => rl.name === loc.value)
            return {
              latitude: loc.latitude || original?.long_lat?.[1] || 0,
              longitude: loc.longitude || original?.long_lat?.[0] || 0,
              zone: loc.label,
            }
          }),
        }

        await addDeliveryPartnerZoneHelper(apiData)
        showAlert('Delivery Zones updated successfully!', 'success')
      }

      setOpen(false)
      fetchDeliveryPartners() // Refresh list
    } catch (error) {
      showAlert(
        error?.message || error || 'Failed to process request.',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleActivate = async (partner, action) => {
    setLoading(true)

    try {
      if (!partner?.id) {
        throw new Error('Invalid delivery partner ID')
      }

      await updateDeliveryPartnerHelper({
        delivery_partner_id: partner.id,
        action: action,
      })

      showAlert(
        `Delivery Partner ${action === 'DELETE' ? 'deleted' : 'updated'} successfully!`,
        'success'
      )
      if (action === 'DELETE') {
        setShowDeleteConfirm(false)
        setPartnerToDelete(null)
      }
      fetchDeliveryPartners() // Refresh list
    } catch (error) {
      showAlert(error?.message || 'Failed to update status', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleCardAction = (partner, action) => {
    switch (action) {
      case 'ACTIVE':
        handleActivate(partner, 'ACTIVE')
        break
      case 'IN_ACTIVE':
        handleActivate(partner, 'IN_ACTIVE')
        break
      case 'DELETE':
        setPartnerToDelete(partner)
        setShowDeleteConfirm(true)
        break
      case 'MANAGE_DRIVER_ZONE':
        handlePartnerClick(partner)
        break
      default:
        break
    }
  }

  return (
    <>
      {isMobile && <MobileHeader headerOptions={headerOptions} />}
      {alertMessage && (
        <Alert
          success={alertType === 'success'}
          alertType={alertType}
          alertMessage={alertMessage}
        />
      )}

      <div className="pageBodyRightPanel">
        {/* Header and Search - similar to PharmacyStore */}
        {!isMobile && (
          <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter mb-5">
            <div className="pageBodyRightPanelHeading">
              Delivery Man Management
            </div>
            <div className="addProductBtn dFlex aiCenter">
              <div
                className={`formGroup mb-0 searchBar expand`}
                onClick={expandSearch}
                onBlur={() => setViewSearch(false)}
              >
                <input
                  ref={inputRef}
                  type="text"
                  className="formControl"
                  placeholder="Search Delivery Name/Number"
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
                <div className={`searchIcon`}>
                  <SearchIcon />
                </div>
              </div>
            </div>
            <button
              className="btn btnPrimary"
              onClick={() => {
                setViewMode('add')
                setOpen(true)
              }}
            >
              <AddIcon /> Add New
            </button>
          </div>
        )}
        {isMobile && (
          <div className="addProductBtnMobile dFlex aiCenter mwebVisible fixed">
            <div
              className={'formGroup mb-0 searchBar expand'}
              onClick={expandSearch}
              onBlur={() => setViewSearch(false)}
            >
              <input
                ref={inputRef}
                type="text"
                className="formControl"
                placeholder="Search Pharmacy Name/Number"
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
              <div className={`searchIcon`}>
                <SearchIcon />
              </div>
            </div>
          </div>
        )}

        <div className="productLlistWrap dFlex">
          {filteredPartners?.map((partner) => {
            return (
              <div className="cardWrap" key={partner.id}>
                <Card
                  userInitials={convertToTitleCase(partner?.name?.[0])}
                  headerSup={partner.name}
                  headerMain={`ID: ${partner.id}`}
                  name={partner.name}
                  phone={partner.mobile_no}
                  email={partner.email}
                  locations={partner.locations}
                  isActive={partner.is_active}
                  // handleClick={() => handlePartnerClick(partner)}
                  cardAction={
                    partner.is_active
                      ? ['IN_ACTIVE', 'DELETE', 'MANAGE_DRIVER_ZONE']
                      : ['ACTIVE', 'DELETE', 'MANAGE_DRIVER_ZONE']
                  }
                  onCardAction={(action) => handleCardAction(partner, action)}
                />
              </div>
            )
          })}
        </div>
      </div>

      <SideDrawer
        open={open}
        handleDrawerToggle={handleDrawerToggle}
        drawerHeading={
          viewMode === 'add' ? 'Add Delivery Partner' : 'Update Delivery Area'
        }
        drawerButton={true}
        handleClick={handleAddSubmit}
        buttonText={viewMode === 'add' ? 'Add' : 'Save' || 'Edit Zone '}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <div>
            <div className="formGroup formFloating">
              <input
                type="text"
                className={`formControl ${errors.name ? 'is-invalid' : ''}`}
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleInputChange}
                readOnly={viewMode === 'edit'} // ✅ not editable in edit mode
              />
              <label>Name</label>
              {errors.name && (
                <div
                  className="invalid-feedback"
                  style={{
                    display: 'block',
                    color: 'red',
                    fontSize: '12px',
                    marginTop: '5px',
                  }}
                >
                  {errors.name}
                </div>
              )}
            </div>

            <div className="formGroup formFloating">
              <input
                type="text"
                className={`formControl ${errors.mobile_no ? 'is-invalid' : ''}`}
                name="mobile_no"
                placeholder="Mobile No"
                value={formData.mobile_no}
                onChange={handleInputChange}
                readOnly={viewMode === 'edit'} // ✅
              />
              <label>Mobile No</label>
              {errors.mobile_no && (
                <div
                  className="invalid-feedback"
                  style={{
                    display: 'block',
                    color: 'red',
                    fontSize: '12px',
                    marginTop: '5px',
                  }}
                >
                  {errors.mobile_no}
                </div>
              )}
            </div>

            <div className="formGroup formFloating">
              <input
                type="email"
                className={`formControl ${errors.email ? 'is-invalid' : ''}`}
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                readOnly={viewMode === 'edit'}
              />
              <label>Email</label>
              {errors.email && (
                <div
                  className="invalid-feedback"
                  style={{
                    display: 'block',
                    color: 'red',
                    fontSize: '12px',
                    marginTop: '5px',
                  }}
                >
                  {errors.email}
                </div>
              )}
            </div>

            {/* Hide password field in edit mode entirely */}
            {viewMode === 'add' && (
              <div className="formGroup formFloating">
                <input
                  type="password"
                  className={`formControl ${errors.password ? 'is-invalid' : ''}`}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <label>Password</label>
                {errors.password && (
                  <div
                    className="invalid-feedback"
                    style={{
                      display: 'block',
                      color: 'red',
                      fontSize: '12px',
                      marginTop: '5px',
                    }}
                  >
                    {errors.password}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Location Select - always visible and editable */}
          <div className="formGroup formFloating">
            <Select
              isMulti
              classNamePrefix="select"
              value={formData.location}
              onChange={handleLocationChange}
              options={rideLocations?.map((loc) => ({
                label: loc.name,
                value: loc.name,
                latitude: loc.long_lat?.[1],
                longitude: loc.long_lat?.[0],
              }))}
              placeholder="Select Delivery Areas"
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: '58px',
                  borderColor: errors.location ? 'red' : base.borderColor,
                  boxShadow: state.isFocused
                    ? '0 0 0 0.2rem rgba(0,123,255,.25)'
                    : null,
                  '&:hover': {
                    borderColor: errors.location ? 'red' : '#ced4da',
                  },
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {errors.location && (
              <div
                className="invalid-feedback"
                style={{
                  display: 'block',
                  color: 'red',
                  fontSize: '12px',
                  marginTop: '5px',
                }}
              >
                {errors.location}
              </div>
            )}
          </div>
        </form>
      </SideDrawer>

      {showDeleteConfirm && (
        <PopupCard
          header="Confirm Deletion"
          body={`Are you sure you want to delete driver: ${partnerToDelete?.name}?`}
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={() => {
              setShowDeleteConfirm(false)
              setPartnerToDelete(null)
            }}
          >
            No
          </button>
          <button
            className="btn btnPrimary ml-5"
            onClick={() => handleActivate(partnerToDelete, 'DELETE')}
          >
            Yes, Delete
          </button>
        </PopupCard>
      )}
    </>
  )
}

export default DeliveryManManagement
